import { useChat } from '@ai-sdk/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { Composer, type ComposerHandle } from './components/Composer';
import { Entrance } from './components/Entrance';
import { FluidBackground } from './components/FluidBackground';
import { HeaderBlob } from './components/HeaderBlob';
import { MessageStream } from './components/MessageStream';
import { RM } from './lib/rm';
import { playLandChime, unlockSound } from './lib/sound';
import { needsWeb } from './lib/websearch';
import { clearChat, loadChat, saveChat } from './lib/persist';
import { readImageAsAttachment, type Attachment } from './lib/attachments';

// A short, friendly label for the active model slug — e.g.
// "nvidia/nemotron-3-ultra-550b-a55b" → "nemotron 3", "claude-sonnet-4-5" → "claude sonnet".
function shortModel(model: string): string {
  const slug = model.split('/').pop() ?? model;
  return slug.split('-').slice(0, 2).join(' ');
}

export default function App() {
  // Restore the last thread (localStorage) so a reload/relaunch doesn't lose it.
  const [restored] = useState(loadChat);
  const { messages, setMessages, input, setInput, handleSubmit, status, error, stop } = useChat({
    api: '/api/chat',
    streamProtocol: 'text',
    // Replies may carry Beads visuals (server/fences.ts); clients without this header get tables.
    headers: { 'x-chat-visuals': '1' },
    initialMessages: restored,
  });
  const busy = status === 'submitted' || status === 'streaming';
  const [stalled, setStalled] = useState(false); // request ran past the stall backstop

  const stageRef = useRef<HTMLElement>(null);
  const composerRef = useRef<ComposerHandle>(null);
  const flipRectRef = useRef<DOMRect | null>(null);
  const [, setEntered] = useState(false);
  const [model, setModel] = useState<string | null>(null);
  const [searching, setSearching] = useState(false); // this turn triggered a live web lookup
  // Image attachments for the next send — ephemeral: converted to base64 data
  // URLs client-side, never uploaded/stored, cleared once the turn is sent.
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [attachError, setAttachError] = useState<string | null>(null);
  // Messages present at mount are restored history — render them settled (no
  // typewriter replay). Reset to 0 on "new chat" so fresh replies animate.
  const [restoredCount, setRestoredCount] = useState(restored.length);

  // Persist the thread whenever it settles (not mid-stream), so completed turns
  // survive a reload. Empty message list clears the store.
  useEffect(() => {
    if (!busy) saveChat(messages);
  }, [busy, messages]);

  // Client-side stall backstop (C): while waiting for the first token (server-side
  // failover handles slow models, but a total stall shouldn't lock the UI), give
  // it STALL_MS; if nothing has streamed by then, abort so the composer unlocks
  // and surface a retry hint. Long replies that have started streaming are exempt.
  const lastMsg = messages[messages.length - 1];
  const waitingFirstToken =
    busy && (!lastMsg || lastMsg.role === 'user' || (lastMsg.role === 'assistant' && lastMsg.content.trim() === ''));
  useEffect(() => {
    if (!waitingFirstToken) return;
    const timer = window.setTimeout(() => {
      stop();
      setStalled(true);
    }, 20000);
    return () => window.clearTimeout(timer);
  }, [waitingFirstToken, stop]);

  // Green-dot action: start a fresh thread. Clears the current conversation and
  // its stored copy; durable facts remain in long-term memory.
  const onNewChat = useCallback(() => {
    setMessages([]);
    clearChat();
    setRestoredCount(0);
    setInput('');
    setSearching(false);
    setStalled(false);
    setAttachments([]);
    setAttachError(null);
    flipRectRef.current = null;
    composerRef.current?.focus();
  }, [setMessages, setInput]);

  // Convert dropped/pasted/picked files to base64 data URLs and stage them for
  // the next send. Best-effort per file — one bad file doesn't block the rest.
  const onAddFiles = useCallback((files: File[]) => {
    setAttachError(null);
    Promise.all(
      files.map((f) =>
        readImageAsAttachment(f).catch((err: Error) => {
          setAttachError(err.message);
          return null;
        }),
      ),
    ).then((results) => {
      const ok = results.filter((a): a is Attachment => a !== null);
      if (ok.length > 0) setAttachments((prev) => [...prev, ...ok].slice(0, 4));
    });
  }, []);

  const onRemoveAttachment = useCallback((index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Learn the live model from the router (truthful, not hardcoded). Best-effort.
  useEffect(() => {
    let alive = true;
    fetch('/api/model')
      .then((r) => r.json() as Promise<{ model?: string | null }>)
      .then((d) => {
        if (alive && d?.model) setModel(d.model);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const onDone = useCallback(() => {
    setEntered(true);
    const stage = stageRef.current;
    if (stage) {
      if (RM) gsap.set(stage, { opacity: 1 });
      else gsap.fromTo(stage, { opacity: 0, y: 16 }, { opacity: 1, y: 0, duration: 0.7, ease: 'expo.out' });
    }
    composerRef.current?.focus();
  }, []);

  const onSend = useCallback(
    (rect: DOMRect) => {
      unlockSound(); // first send is the user gesture that lets the chime play
      setStalled(false); // clear any prior stall notice
      setSearching(needsWeb(input)); // reflect a web lookup in the pending indicator
      flipRectRef.current = rect;
      const atts = attachments;
      setAttachments([]);
      handleSubmit(undefined, atts.length > 0 ? { experimental_attachments: atts } : undefined);
    },
    [handleSubmit, input, attachments],
  );

  const onLanded = useCallback(() => playLandChime(), []);

  // Surface any failure to the user, even when the error carries no message
  // (e.g. an upstream error with an empty body) — otherwise the turn fails silently.
  const errorText = stalled
    ? 'That took too long to respond. Please try again.'
    : error
      ? error.message || 'Something went wrong. Please try again.'
      : undefined;

  return (
    <>
      <FluidBackground />
      <div className="grain" />
      <Entrance onDone={onDone} />
      <main className="stage" ref={stageRef} style={{ opacity: 0 }}>
        <div className="head">
          <button type="button" className="newchat" onClick={onNewChat} aria-label="Start a new chat">
            <HeaderBlob active={busy} />
            <span className="newchat-tip" role="tooltip" aria-hidden="true">
              <span className="newchat-tip-t">New chat</span>
              <span className="newchat-tip-d">Start fresh — clears this thread. Your memory is kept.</span>
            </span>
          </button>
          <span className="mark">neuro</span>
          {model && (
            <span className="model" title={model}>
              {shortModel(model)}
            </span>
          )}
        </div>
        <MessageStream messages={messages} busy={busy} searching={searching} restoredCount={restoredCount} error={errorText} flipRectRef={flipRectRef} onLanded={onLanded} />
        {attachError && <div className="attach-error">{attachError}</div>}
        <Composer
          ref={composerRef}
          value={input}
          onChange={setInput}
          onSend={onSend}
          busy={busy}
          attachments={attachments}
          onAddFiles={onAddFiles}
          onRemoveAttachment={onRemoveAttachment}
        />
      </main>
    </>
  );
}
